"use client";

import { useState } from "react";
import { IBrandLogoGridRaw } from "@/types/brandLogoGrid";
import Button from "@/components/ui/button/Button";
import LogoCarousel from "./LogoCarousel";
import LogoCarouselSingleRow from "./LogoCarouselSingleRow";
import LogoGrid from "./LogoGrid";
import LogoMarquee from "./LogoMarquee";
import CollapsibleText from "./CollapsibleText";

interface BrandLogoGridProps {
  data: IBrandLogoGridRaw;
  locale: string;
}

function resolveLink(
  link: { type?: string; url?: string } | undefined,
  locale: string,
  fallback: string,
): string {
  if (!link?.url) return fallback;
  if (link.type === "external") return link.url;
  return `/${locale}${link.url.startsWith("/") ? "" : "/"}${link.url}`;
}

interface BrandLogoHeaderProps {
  title?: string;
  introText?: string;
  readMoreSettings?: any;
  isRtl: boolean;
}

function BrandLogoHeader({
  title,
  introText,
  readMoreSettings,
  isRtl,
}: BrandLogoHeaderProps) {
  if (!title && !introText) return null;

  return (
    <div className="text-center w-full max-w-7xl mx-auto mb-8 md:mb-12 px-4">
      {title && (
        <h2 className="text-lg md:text-2xl lg:text-3xl font-semibold text-gray-900 leading-tight">
          {title}
        </h2>
      )}
      {introText && (
        <CollapsibleText text={introText} readMoreSettings={readMoreSettings} isRtl={isRtl} />
      )}
    </div>
  );
}

export default function BrandLogoGrid({ data, locale }: BrandLogoGridProps) {
  const {
    enable,
    title,
    introText,
    viewLessLabel,
    readMoreSettings,
    displayVariant = "carousel",
    carouselRows = "two",
    marqueeSettings,
    viewAllButton,
    brands: rawBrands = [],
  } = data;

  const [isExpanded, setIsExpanded] = useState(false);
  const isRtl = locale === "ar";

  if (enable === false) return null;

  const brands = rawBrands
    .filter((brand) => !!brand.logoUrl)
    .map((brand, idx) => {
      const brandHref =
        brand.enableLink !== false
          ? resolveLink(brand.link, locale, "")
          : "";

      return {
        _key: brand.key || `brand-${idx}`,
        logo: { url: brand.logoUrl! },
        link: brandHref ? { url: brandHref } : undefined,
      };
    });

  if (brands.length === 0 && !title && !introText) return null;

  // Toggle button labels
  const expandLabel = viewAllButton || (isRtl ? "عرض الكل" : "View All");
  const collapseLabel = viewLessLabel || (isRtl ? "عرض أقل" : "View Less");

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const renderComponent = () => {
    // If expanded, render all brands in a static grid layout
    if (isExpanded) {
      return (
        <LogoGrid
          brands={brands}
          viewAllLabel=""
          viewAllHref=""
          locale={locale}
        />
      );
    }

    // ── Marquee variant ────────────────────────────────────────────────────────
    if (displayVariant === "marquee") {
      return (
        <LogoMarquee
          brands={brands}
          marqueeSettings={marqueeSettings}
          viewAllLabel=""
          viewAllHref=""
          locale={locale}
        />
      );
    }

    // ── Static Grid variant ────────────────────────────────────────────────────
    if (displayVariant === "grid") {
      // Collapse state: show at most 12 logos
      const collapsedBrands = brands.slice(0, 12);
      return (
        <LogoGrid
          brands={collapsedBrands}
          viewAllLabel=""
          viewAllHref=""
          locale={locale}
        />
      );
    }

    // ── Carousel variant (default) ─────────────────────────────────────────────
    const sharedCarouselProps = {
      brands,
      viewAllLabel: "",
      viewAllHref: "",
      locale,
    };

    if (carouselRows === "one") {
      return <LogoCarouselSingleRow {...sharedCarouselProps} />;
    }

    return <LogoCarousel {...sharedCarouselProps} />;
  };

  // Determine if we should show the toggle button in the collapsed state
  const shouldShowToggle =
    displayVariant === "grid" ? brands.length > 12 : brands.length > 0;

  return (
    <div className={`py-12 md:py-16 ${displayVariant === "marquee" && !isExpanded ? "overflow-hidden" : "px-4 md:px-8"}`}>
      {(title || introText) && (
        <BrandLogoHeader
          title={title}
          introText={introText}
          readMoreSettings={readMoreSettings}
          isRtl={isRtl}
        />
      )}
      {renderComponent()}

      {/* Expand/Collapse Toggle Button */}
      {(isExpanded || shouldShowToggle) && (
        <div className="mx-auto flex w-full max-w-[10.625rem] items-center justify-center pt-7 md:pt-9">
          <Button
            variant="contained"
            className="flex h-[2.5rem] w-full items-center justify-center text-sm font-semibold md:h-[3.125rem]"
            onClick={handleToggle}
          >
            {isExpanded ? collapseLabel : expandLabel}
          </Button>
        </div>
      )}
    </div>
  );
}
